import { useEffect, useRef, type RefObject } from 'react';

/**
 * Returns a ref to attach to `FormErrorBanner` and automatically scrolls
 * the banner into view whenever `bannerVisible` transitions to `true`.
 *
 * Using a `useEffect` instead of calling `scrollIntoView` inline in the
 * event handler avoids a timing bug: `FormErrorBanner` renders `null` when
 * `visible` is false, so the ref is `null` at the moment the handler fires.
 * The effect runs after React has re-rendered the banner into the DOM.
 *
 * @example
 * const bannerRef = useScrollToError(bannerVisible);
 * // …
 * <FormErrorBanner ref={bannerRef} visible={bannerVisible} />
 */
export function useScrollToError(bannerVisible: boolean): RefObject<HTMLDivElement | null> {
  const bannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (bannerVisible) {
      bannerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [bannerVisible]);

  return bannerRef;
}
